"use client";

import React from "react";
import { useRouter, useSearchParams } from "next/navigation";

interface DeptFilterProps {
  /** Departments that opted into the public site (from
   * `/core/departments/public`). May be empty — the dropdown then
   * only shows All + University-wide. */
  departments?: any[];
  /** Currently selected `?dept=` value — `all`, `org`, or a dept id. */
  selectedDept?: string;
  /** Route base for the current post type, e.g. `/post/news`. */
  basePath: string;
  /** Language for the option labels (en/mm). Defaults to English. */
  currentLanguage?: "en" | "mm";
}

/** Bilingual fixed labels for the two synthetic options. */
const LABELS = {
  all: { en: "All", mm: "အားလုံး" },
  org: { en: "University-wide", mm: "တက္ကသိုလ်အဆင့်" },
} as const;

/** Resolve a department's display label for the given language,
 *  falling back through displayName → fullName → shortName → slug. */
function deptLabel(dept: any, language: "en" | "mm"): string {
  const dn = dept?.displayName;
  if (dn && typeof dn === "object") {
    const v = language === "mm" ? dn.mm || dn.en : dn.en || dn.mm;
    if (v) return v;
  }
  return (
    dept?.displayShortName ||
    dept?.fullName ||
    dept?.shortName ||
    dept?.slug ||
    String(dept?._id ?? "")
  );
}

/**
 * Department dropdown filter for the generic post-list pages. Updates
 * the `?dept=` URL param (and resets `?page=1`, preserving `view`/`limit`)
 * so the server component re-fetches the scoped list. Theme-neutral —
 * styled via CSS variables so both themes can share it.
 */
export function DeptFilter({
  departments = [],
  selectedDept = "all",
  basePath,
  currentLanguage = "en",
}: DeptFilterProps) {
  const router = useRouter();
  const params = useSearchParams();

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    const sp = new URLSearchParams(params.toString());
    if (!next || next === "all") {
      sp.delete("dept");
    } else {
      sp.set("dept", next);
    }
    // New filter → restart paging from the first page. Existing
    // view/limit params are carried along by reusing the current
    // URLSearchParams.
    sp.delete("page");
    const qs = sp.toString();
    router.push(qs ? `${basePath}?${qs}` : basePath);
  };

  const hasDepartments = Array.isArray(departments) && departments.length > 0;

  return (
    <select
      value={selectedDept}
      onChange={onChange}
      aria-label={currentLanguage === "mm" ? "ဌာနဖြင့် စစ်ထုတ်ရန်" : "Filter by department"}
      className="h-8 rounded-md border px-2 text-sm"
      style={{
        borderColor: "var(--color-border)",
        backgroundColor: "var(--color-background)",
        color: "var(--color-foreground)",
      }}
    >
      <option value="all">{LABELS.all[currentLanguage]}</option>
      <option value="org">{LABELS.org[currentLanguage]}</option>
      {hasDepartments
        ? departments.map((d: any) => (
            <option key={String(d?._id)} value={String(d?._id)}>
              {deptLabel(d, currentLanguage)}
            </option>
          ))
        : null}
    </select>
  );
}

export default DeptFilter;
